import React, { Key, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Dialog, DialogActions, DialogContent, DialogTitle, Slide } from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'

import { IFullOffertInfoModalInstalmentScheduleItemProps, IFullOffertInfoModalInstalmentsDetailsProps, IFullOffertInfoModalProps, IFullOffertInfoModalSummaryProps } from './FullOffertInfoModalProps'
import Button, { AppButtonTypes } from 'components/Buttons/ButtonBase/Button'
import { numberToDisplayString } from 'common/helpers/numberHelpers'
import { AppState } from 'store'
import { PaymentSchedule } from 'models/prepare.offert.model.dto'
import useIsMobile from 'common/hooks/useIsMobile'
import { getLastNotSelectedMonth } from 'store/InstalmentsMonthsSlice/instalmentsMonthHelperFunctions'
import { paymentDateToDisplayString } from 'common/helpers/dateHelpers'
import useTextContents from 'common/hooks/useTextContents'
import { DateTime } from 'luxon'
import analyticsService from 'service/AnalyticsService/AnalyticsService'
import { isTrastiBroker } from 'common/helpers/helpers'

import FullOffertMenuCloseIcon from '../../assets/icons/full-offert-info-menu-close-icon.svg'
import './FullOffertInfoModalStyles.scss'
import './safari.css'


const FullOffertInfoModalSummary = (props: IFullOffertInfoModalSummaryProps) => {

   const { IS_TRASTI, amountToPay, billingCompany, fullOffertInfoTextContents } = props

   return (
      <div className='full-offert-info-modal-summary-wrapper' >
         <h4 className='full-offert-info-modal-summary-title'>{fullOffertInfoTextContents('modals.fullOffertInfoModal.summary.header')}</h4>
         <div className='full-offert-info-modal-summary-item' >
            <p>{fullOffertInfoTextContents('modals.fullOffertInfoModal.summary.summaryItem1')}</p>
            <p>{IS_TRASTI? fullOffertInfoTextContents('modals.fullOffertInfoModal.trastiBrokerVersion.billingCompanyName') : billingCompany}</p>
         </div>
         <div className='full-offert-info-modal-summary-item' >
            <p>{fullOffertInfoTextContents('modals.fullOffertInfoModal.summary.summaryItem2')}</p>
            <p>{numberToDisplayString(amountToPay, " zł")}</p>
         </div>
      </div>
   )
}

const FullOffertInfoModalInstalmentScheduleItem = (props: IFullOffertInfoModalInstalmentScheduleItemProps) => {

   const {index, singlePaymentSchedule, instalmentAmount, installmentNumberToDisplay } = props

   if(singlePaymentSchedule.isSelected) {
      return (
         <div key={index} className='instalments-details-repayment-by-instalments-schedule-item'>
            <p>{`${installmentNumberToDisplay} spłata`}</p> 
            <p className='schedule-item-selected'>{`do ${paymentDateToDisplayString(singlePaymentSchedule.paymentDate)}`}</p>
            <p className='schedule-item-selected'>{numberToDisplayString(singlePaymentSchedule.installmentAmount.toFixed(2), ' zł')}</p>
         </div>
      )
   }
   else{ 
      return (
         <div key={index} className='instalments-details-repayment-by-instalments-schedule-item'>
            <p className='schedule-item-unselect-placeholder'></p>
            <p className='schedule-item-unselected'>{`do ${paymentDateToDisplayString(singlePaymentSchedule.paymentDate)}`}</p>
            <p className='schedule-item-unselected'>{numberToDisplayString(0, ' zł')}</p>
         </div>
      )
   }
}

const FullOffertInfoModalInstalmentDetails = (props: IFullOffertInfoModalInstalmentsDetailsProps) => {

   const { IS_TRASTI, currentOffert, fullOffertInfoTextContents } = props
   const repaymentFinalDate = paymentDateToDisplayString(DateTime.fromISO(currentOffert.paymentSchedule[0].paymentDate).minus(1).toString()) // Data pierwszej raty - 1 dzień
   let installmentCount = 0; // Initial installment number


   return (
      <div className='full-offert-info-modal-details-wrapper full-offert-info-modal-safari-only'>
         <h4 className='full-offert-info-modal-details-title'>{fullOffertInfoTextContents('modals.fullOffertInfoModal.instalmentDetails.header')}</h4>
         {
            !IS_TRASTI && (
               <div className='instalments-details-repayment-in-time-outer-wrapper'>
                  <div className='instalments-details-heading instalments-details-repayment-in-time-heading'>
                     <p>{fullOffertInfoTextContents('modals.fullOffertInfoModal.instalmentDetails.standardPeriod.header')}</p>
                     <span className='instalments-details-heading-line' />
                  </div>
                  <p className='instalments-details-repayment-in-time-col-1'>{fullOffertInfoTextContents('modals.fullOffertInfoModal.instalmentDetails.standardPeriod.column1')}</p>
                  <p className='instalments-details-repayment-in-time-col-2'>{`do ${repaymentFinalDate}`}</p>
                  <p className='instalments-details-repayment-in-time-col-3'>{numberToDisplayString(currentOffert.mininalAmountToPay, ' zł')}</p>
                  <p className='instalments-details-repayment-in-time-summary full-offert-instalments-details-summary-safari-only'>
                     {
                        `Łącznie do spłaty ${numberToDisplayString(currentOffert.mininalAmountToPay, ' zł')} 
                        w tym całkowita kwota transzy ${numberToDisplayString(currentOffert.loanAmountNet, ' zł')} 
                        oraz prowizja ${numberToDisplayString(currentOffert.minimalCommisionAmount, ' zł')},
                        oprocentowanie 0%, RRSO ${numberToDisplayString(currentOffert.earlyRRSO, '%')}.`
                     }
                  </p>
               </div>
            )
         }
         <div className='instalments-details-repayment-by-instalments-outer-wrapper'>
            <div className='instalments-details-heading instalments-details-repayment-by-instalments-heading'>
               {
                  IS_TRASTI ?
                  <p>{fullOffertInfoTextContents('modals.fullOffertInfoModal.instalmentDetails.afterStandardPeriodHeaderTextTrastiVersion')}</p> :
                  <p>{fullOffertInfoTextContents('modals.fullOffertInfoModal.instalmentDetails.afterStandardPeriodHeaderText')}</p>
               }
               <span className='instalments-details-heading-line' />
            </div>
            <div className='instalments-details-repayment-by-instalments-schedule-container'>
               {
                  currentOffert.paymentSchedule
                  .filter((singlePaymentSchedule: PaymentSchedule) => { // Nie wyświetlamy w harmonogramie ostatnich nie wybranych rat.
                     const lastNotSelectedMonthsInstallmentNumbers = getLastNotSelectedMonth(currentOffert.paymentSchedule)
                     return !lastNotSelectedMonthsInstallmentNumbers.includes(singlePaymentSchedule.installmentNumber)
                  })
                  .map((singlePaymentSchedule: PaymentSchedule, index: Key) => {
                     return (
                        <FullOffertInfoModalInstalmentScheduleItem 
                           index={index} 
                           singlePaymentSchedule={singlePaymentSchedule} 
                           instalmentAmount={currentOffert.instalmentAmount}
                           installmentNumberToDisplay={singlePaymentSchedule.isSelected ? ++installmentCount : installmentCount}
                        />
                     )
                  })
               }
            </div>      
            <p className='instalments-details-repayment-by-instalments-summary full-offert-instalments-details-summary-safari-only'>
               Łącznie do spłaty <span style={{whiteSpace: 'nowrap'}}>{numberToDisplayString(currentOffert.ckdz, ' zł ')}</span> 
               w tym całkowita kwota transzy <span style={{whiteSpace: 'nowrap'}}>{numberToDisplayString(currentOffert.loanAmountNet, ' zł ')}</span> 
               oraz prowizja <span style={{whiteSpace: 'nowrap'}}>{numberToDisplayString(currentOffert.loanAmountGross - currentOffert.loanAmountNet, ' zł')}, </span> 
               oprocentowanie stałe <span style={{whiteSpace: 'nowrap'}}>{numberToDisplayString(currentOffert.interestRate, '%')}, </span> 
               <span style={{whiteSpace: 'nowrap'}}>RRSO {numberToDisplayString(currentOffert.rrso, '%')}.</span> 
            </p>   
         </div>
      </div>
   )
}


/**
 * Link do dokumentacji: https://mui.com/material-ui/api/slide/
*/
const FullOffertInfoModalDialogTransition = React.forwardRef(function Transition(props: TransitionProps & {children: React.ReactElement<any, any>}, ref: React.Ref<unknown>) {
   return <Slide direction="left" ref={ref} {...props} />
})
 

const FullOffertInfoModal = (props: IFullOffertInfoModalProps) => {
   const applicationState = useSelector((state: AppState) => state.applicationState)
   const analyticsEventTypesNames = useSelector((state: AppState) => state.globalConfigurationState.analyticsEventTypesNames)
   const isMobileDevice = useIsMobile()
   const fullOffertInfoTextContents = useTextContents()


   useEffect(() => {
      if(props.isOpen && analyticsEventTypesNames !== undefined){
         analyticsService.sendAnalyticEvent(analyticsEventTypesNames.PaymentScheduleShow) // Odkładamy dane statystyczne o wejściu użytkownika na modala z harmonogramem płatności
      }
   }, [ props.isOpen ])

   return (
      <Dialog
         scroll={'body'}
         fullScreen={isMobileDevice ? true : false}
         TransitionComponent={FullOffertInfoModalDialogTransition}
         open={props.isOpen}
         onBlur={event  => {}}
         onFocus={event => {}}
         onClose={props.handleOnClose}
         classes={{
            root: 'full-offert-info-modal-outer-root-wrapper',
            paper: 'full-offert-info-modal-container',
         }}
      >
         {
            isMobileDevice ? 
            <DialogTitle classes={{ root: 'full-offert-info-modal-menu-title' }}>
               <img src={FullOffertMenuCloseIcon} alt="x" onClick={props.handleOnClose}/>
            </DialogTitle>
            : 
            null
         }
         <DialogContent classes={{ root: 'full-offert-info-modal-content-wrapper' }}>
            <FullOffertInfoModalSummary 
               billingCompany={applicationState.billingCompany} amountToPay={applicationState.amountToPay} 
               fullOffertInfoTextContents={fullOffertInfoTextContents} IS_TRASTI={isTrastiBroker()} 
            />
            <FullOffertInfoModalInstalmentDetails currentOffert={applicationState.offert} fullOffertInfoTextContents={fullOffertInfoTextContents} IS_TRASTI={isTrastiBroker()} />
         </DialogContent> 
         <DialogActions classes={{ root: 'full-offert-info-modal-bottom-back-button full-offert-info-modal-bottom-back-button-safari-only'}} onClick={props.handleOnClose}>
            <Button uiButtonType={AppButtonTypes.PRIMARY} buttonContent={fullOffertInfoTextContents('modals.fullOffertInfoModal.submitButtonText')} className='full-offert-info-modal-button-close'/>
         </DialogActions>
      </Dialog>
   )
}

export default FullOffertInfoModal